import { ActionIcon, Badge, Button, Card, Collapse, createTheme, Table } from '@mantine/core';
import type { CSSVariablesResolver, MantineBreakpoint, MantineColorsTuple } from '@mantine/core';

/**
 * GÖRSEL DİL — **TEK KAYNAK** (F4 · görsel dil turu, kararlar G1-G8;
 * Kapsam §20.3b · Teknoloji §4.12).
 *
 * > **GÖRSEL DEĞER TEK YERDEN OKUNUR.** Çapa rengi · zemin · anlam renkleri ·
 * > risk ölçeği · tipografi ölçeği · boşluk · köşe yarıçapı · dokunma tabanı —
 * > hepsi BU dosyadan çıkar. Ekranda renk ya da boyut sabitleyen kod **ikinci
 * > bir kaynak** üretir ve görsel dil turunu tek dosyalık bir işten yüz
 * > ekranlık bir işe çevirir.
 *
 * ⚠️ **index.css ARTIK RENK TAŞIMAZ.** Eskiden `:root`ta duran `--isg-*`
 * değişkenleri buradan **`isgCssDegiskenleri`** ile üretilir (MantineProvider
 * `cssVariablesResolver`). Böylece TS tarafı ile CSS tarafı **aynı sabitten**
 * beslenir; ikisini elle eşitleme borcu ortadan kalkar.
 *
 * ⚠️ **ESKİ TUZAK, YENİ ÇÖZÜM (kayıt düşülüyor — bilgi kaybolmasın):** Mantine
 * tema değişkenlerini çalışma zamanında `:root`a basar ve **kaynak sırasıyla**
 * bizim `:root` bloğumuzu ezerdi; bu yüzden köprü değişkenleri `body`
 * KAPSAMINDA tanımlanmıştı. Artık değişkenler **Mantine'ın kendi bloğundan**
 * çıktığı için çakışacak iki blok yok — hack kaldırıldı, sebebi burada durur.
 */

/* ══════════════════════════════════════════════════════════════════════════
   1. RENK — çapa · zemin · yüzey
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * **G1 — ÇAPA RENGİ: lacivert, bir tık AÇIK.** ~~Başlık çubuğu · birincil düğme ·
 * vurgu.~~ Eski #1B2A4A ürün sahibi tarafından *"fazla koyu"* bulundu; ton
 * korundu, değer bir basamak açıldı.
 *
 * ✏️ **REVİZE 07.10.2026 (Genel bakış düzeni + kabuk renkleri — ürün sahibi;
 * kural SİLİNMEDİ, rolü daraldı): ÇAPA ARTIK BİRİNCİL RENK DEĞİLDİR.** Birincil
 * düğme · bağlantı · vurgu `brand` paletinden okunur (aşağıda — `PLATFORM
 * AKSANI`); menünün üç dolgusu da artık lacivert skalasından değil, koyu
 * menünün kendi token'larından gelir. `lacivert` skalası kayıtlı kalır ve
 * `--isg-capa` üretilmeye devam eder: bugünkü tek okuyucusu `METIN_ANA`nın
 * akrabası olan başlık tonudur ve `gorunum-artcisi.spec` negatif kanaryası
 * (*bant çapayı geri okumaz*). Silinseydi kanarya ölçtüğü şeyi kaybederdi.
 */
const CAPA = '#2E4470';

/**
 * **G10 — ÜST ÇUBUK ÇAPA RENGİNDEDİR** (görünüm artçısı, 27.08.2026). Çubuk
 * beyazken kabuk *"başlıksız"* duruyordu: marka rengi üründe hiçbir yerde
 * geniş bir yüzey tutmuyor, yalnız düğmelerde nokta nokta görünüyordu.
 *
 * Çapa üzerine düşen metin AYRI TOKENDIR — beyaz "varsayılan" değil, bir
 * karardır: çapa açıldığı gün (G1'de bir kez açıldı) bu iki değerin AA
 * eşiğini hâlâ karşıladığı **tek yerden** doğrulanabilsin.
 * Ölçüm: beyaz 9.6:1 · ikincil 5.7:1.
 */
const CAPA_UZERI_METIN = '#FFFFFF';
const CAPA_UZERI_IKINCIL = '#BFC8DC';

/**
 * **G2 — ZEMİN KIRIK BEYAZ, KART BEYAZ.** Zemin griye yakın kırık beyazdır
 * (sarıya değil — *"eski"* hissi vermez). **Kart beyaz kalır ki zeminden
 * ayrışsın:** yoğun tabloda satır okunurluğu bu ayrışmaya bağlıdır; kartı da
 * zemin rengine boyayan tasarım, tabloyu zeminin içinde eritir.
 */
const ZEMIN = '#F6F4EF';
const YUZEY = '#FFFFFF';
const KENARLIK = '#E5E1D8';
const METIN_ANA = '#1B2A4A';
/**
 * İkincil metin. ⚠️ Eski değer (#6B7785) yeni zemin üzerinde **4.15:1** ile
 * WCAG AA eşiğinin (4.5) altına düşüyordu — ton korunarak koyulaştırıldı
 * (zeminde 5.4:1). Bu, tema token turunun `unknown` düzeltmesinin aynısıdır.
 */
const METIN_IKINCIL = '#5D6975';

/**
 * **MARKA AKSENTİ — YALNIZ MARKA YÜZEYİ** (logo şeridi, seçili sekme).
 * ⚠️ Durum renkleri bundan **OKUNMAZ** (aşağıdaki G7 bloğu): white-label'da
 * müşteri aksenti değiştiğinde uyarı renkleri **KAYMAZ**.
 */
const AKSENT = '#E8631A';

/**
 * **BANT ZEMİNİ — ÜST ÇUBUĞUN KENDİ DEĞERİ (18.09.2026, ürün sahibi).**
 *
 * ⚠️ **ÇAPADAN AYRI BİR DEĞERDİR, ONUN YERİNE GEÇMEZ.** `CAPA` aynı zamanda
 * `lacivert` skalasının tabanıdır (birincil düğme · vurgu · seçili satır);
 * bandı koyulaştırmak için onu değiştirmek **ürünün tamamını** koyulaştırırdı.
 * Bu token'ın TEK okuyucusu `index.css`in üst çubuk kuralıdır — başka hiçbir
 * yüzey bu değeri okumaz.
 *
 * Çubuk üzeri metin token'ları (`CAPA_UZERI_*`) DEĞİŞMEDİ: beyaz bu zeminde
 * 19.4:1, ikincil 11.9:1 — ikisi de AA eşiğinin çok üstünde.
 *
 * ✏️ **07.10.2026 (Genel bakış düzeni + kabuk renkleri — ürün sahibi): BANT VE
 * MENÜ TEK L ÇERÇEVEDİR.** ~~`#050A30`~~ → **`#060A2E`**; sol menü de bu
 * zemini okur (`MENU_ZEMIN = BANT_ZEMIN`, aşağıda). "Tek okuyucusu üst çubuk"
 * cümlesi bu tarihten itibaren eskidir — iki okuyucu var ve ikisi aynı değeri
 * ister; ikinci bir sabit yazılmaz.
 */
const BANT_ZEMIN = '#060A2E';

/**
 * **G9 — SOL MENÜ** ~~ZEMİNİN AYNISI~~ (görünüm artçısı, 27.08.2026). Menü
 * beyazken zeminle yarışıyor ve *"solda duran devasa bir kart"* gibi duruyordu;
 * 27.08'de menü ZEMİN'e boyandı ve ikinci bir token bilinçli açılmadı.
 *
 * ✏️ **REVİZE 07.10.2026 (ürün sahibi — Stitch taslağının SafeOps'a uyarlanması;
 * kural SİLİNMEDİ, bağlandığı değer değişti): MENÜ KOYUDUR, ZEMİNİ ÜST BARIN
 * ZEMİNİDİR.** Bant + menü tek L çerçeve çizer; içerik alanı kırık beyaz zemin
 * ve beyaz kartlarla öne çıkar — G9'un *"menü geri çekilir"* amacı aynen, aracı
 * değişti. ⚠️ **AYRI BİR DEĞER HÂLÂ DEĞİL:** 27.08'in *"ikinci token açılmaz"*
 * kilidi korunur — menü zemini bir başka yüzeyin (bandın) kendisidir.
 *
 * ⚠️ **MENÜ İKON TAŞIMAZ, SAYAÇ TAŞIMAZ** (ürün sahibi — bilinçli red:
 * kalabalık). Hiyerarşi renkle değil **boyut · ağırlık · harf aralığıyla**
 * kurulur (başlık küçük, büyük harf, geniş aralık — mevcut kural).
 */
const MENU_ZEMIN = BANT_ZEMIN;

/**
 * **MENÜ KONTRASTI — WCAG AA, `#060A2E` ZEMİN ÜZERİNDE ÖLÇÜLDÜ** (07.10.2026).
 *
 * | Öğe            | Değer     | Kontrast |
 * | Grup başlığı   | `#94A3B8` | 7,5:1    |
 * | Alt kalem      | `#CBD5E1` | 13:1     |
 * | Aktif kalem    | `#FFFFFF` | 19:1     |
 * | Hover / aktif dolgusu | `#161C4A` | — (yüzey) |
 *
 * ⚠️ **YASAK TON `#64748B` — 4,0:1, AA'yı GEÇMEZ.** Soluk gri "ikincil metin"
 * refleksi koyu zeminde eşiğin altına düşer; grup başlığı bu yüzden bir
 * basamak açık tutulur. Kontrast kanaryası `genel-bakis-duzen.spec` üç metin
 * tonunu zemine karşı HESAPLAR (yasak ton negatif kontrol).
 *
 * ⚠️ **AKTİF KALEM OKUNURLUĞU AKSANA BAĞLI DEĞİLDİR:** dolgu + beyaz yazı
 * okunurluğu taşır; aksan (`AKSAN_KOYU_ZEMIN`) yalnız soldaki **ince çubukta**
 * durur. White-label'da aksan değişince menü okunur kalır.
 *
 * ⚠️ **HOVER VE AKTİF DOLGUSU AYNI DEĞERDİR** (ürün sahibi tablosu). Kenar
 * çubuğunun *"üç durum üç işaret"* kuralı (§10) AYNEN geçerli: aktif kalem
 * dolguyu hover'la paylaşır ama **yazı rengi (beyaz) · sol çubuk · ağırlık**
 * üçüyle ayrışır. 20.09'un *"aktif dolgu hover'dan bir basamak koyu"* cümlesi
 * bu tarihten itibaren eskidir (revizyon izi `.claude/rules/gorsel-dil.md` §10.2).
 */
const MENU_METIN = '#CBD5E1';
const MENU_GRUP_BASLIK = '#94A3B8';
const MENU_HOVER_DOLGU = '#161C4A';
const MENU_AKTIF_DOLGU = MENU_HOVER_DOLGU;
const MENU_AKTIF_METIN = '#FFFFFF';

/** Kontrast kanaryası ve belge için dışa açık okuma — ekran bunları yazmaz, token'ı okur. */
export const MENU_RENKLERI = {
  zemin: MENU_ZEMIN,
  metin: MENU_METIN,
  grupBaslik: MENU_GRUP_BASLIK,
  hoverDolgu: MENU_HOVER_DOLGU,
  aktifDolgu: MENU_AKTIF_DOLGU,
  aktifMetin: MENU_AKTIF_METIN,
  /** AA'yı geçmeyen ton — menüde KULLANILMAZ; kanaryanın negatif kontrolü. */
  yasakTon: '#64748B',
} as const;

/**
 * **PLATFORM AKSANI — MAVİ, İKİ TON** (07.10.2026, ürün sahibi; Kapsam §15.2 ✏️).
 *
 * `brand` Mantine'a **birincil renk** olarak kayıtlıdır (`primaryColor: 'brand'`,
 * `primaryShade: 6`). Palet **verilen değerlerdir, türetilmez**: white-label'da
 * müşterinin değiştirebileceği şey bu on değerin kendisidir (`skala()` ürünün
 * iç renklerini türetir; marka paleti bir GİRDİDİR).
 *
 * | Zemin | Ton         | Değer     | Kontrast           |
 * | açık  | `brand[6]`  | `#2563EB` | beyazda 5,2:1      | düğme · bağlantı
 * | koyu  | `brand[4]`  | `#60A5FA` | `#060A2E` üzerinde 7,6:1 | menü aktif çizgisi · bant/menü bağlantıları
 *
 * ⚠️ **`#2563EB` KOYU ZEMİNDE KULLANILMAZ** (3,7:1 — AA'yı geçmez). Bant ve
 * menü kapsamında bağlantı rengi `AKSAN_KOYU_ZEMIN`den okunur (index.css,
 * `--mantine-color-anchor` yeniden tanımı).
 *
 * ⚠️ **WHITE-LABEL SINIRI TOKEN DÜZEYİNDE AYRILIR** (Kapsam §15.2 revizyonu):
 * müşterinin değiştirebileceği → **logo + `brand` paleti**. SABİT (white-label
 * dışı) → **menü/üst bar zemini ve yazı tonları** (`MENU_RENKLERI` · `BANT_ZEMIN`
 * · `CAPA_UZERI_*`) ve **durum paleti** (`ANLAM_TABANI` — G7'nin *"aksentten
 * türetilmez"* kuralı aynen). White-label renk üretici ve kontrast kontrolü bu
 * işte YAPILMADI (açık uç — session-log).
 */
const BRAND_PALETI: MantineColorsTuple = [
  '#EFF6FF',
  '#DBEAFE',
  '#BFDBFE',
  '#93C5FD',
  '#60A5FA',
  '#3B82F6',
  '#2563EB',
  '#1D4ED8',
  '#1E40AF',
  '#1E3A8A',
];
/** Mantine `primaryShade` — `brand[6]` açık zeminin aksanıdır; `skala()` da tabanı bu basamağa oturtur. */
export const TABAN_BASAMAGI = 6;
export const AKSAN_ACIK_ZEMIN = BRAND_PALETI[TABAN_BASAMAGI];
export const AKSAN_KOYU_ZEMIN = BRAND_PALETI[4];

/**
 * **BANT YÜKSEKLİĞİ** (logo bandı; G10'un 56px'i → 112 → **72**, 19.09.2026).
 * ⚠️ Bant artık bir başlık satırı değil bir **marka yüzeyidir**; yükseklik
 * `AppShell`in `header.height`ine buradan iner (iki kabuk de aynı değeri
 * okur — portal bandı ayrı bir ölçü taşımaz).
 * ⚠️ **TEK SABİT:** içerik alanının üst payı ve kenar çubuğunun üst ofseti
 * Mantine'ın `AppShell`i tarafından **bu prop'tan** türetilir
 * (`--app-shell-header-offset`) — hiçbir ekran bant yüksekliğini elle
 * yazmaz, `calc(100vh - …)` hesabı yoktur. Değeri değiştirmek tek satırdır.
 */
export const BANT_YUKSEKLIK_PX = 72;

/**
 * Bantta logonun yüksekliği (genişlik oranla türetilir — `w="auto"`).
 *
 * ✏️ **19.09.2026 · 72 → 48 → 36 (logo iç boşluğu + ölçü ayarı).** Değer küçülmedi,
 * **DÜRÜSTLEŞTİ:** kaynak PNG dört kenarında şeffaf pay taşıyordu (dolu
 * kutu 1392×296, dosya 2060×826 — dikeyde yalnız **%35,8**), yani `h=72`
 * ekranda **25,8px** logo çiziyordu. Dosya sınır kutusuna kırpıldı
 * (`*-kaynak.png` yanında durur); artık verilen yükseklik **görünen**
 * yüksekliktir.
 * ⚠️ **MARKA VARLIĞI KENAR PAYSIZ SAKLANIR** — boşluk bileşende verilir;
 * bileşende negatif margin / `scale` / `object-fit` hilesi YOK.
 */
export const BANT_LOGO_YUKSEKLIK_PX = 36;

/**
 * **DEMO ŞERİDİ YÜKSEKLİĞİ** (demo ortamı, 24.09.2026 — `shell/DemoSeridi.tsx`).
 * ✏️ 25.09.2026: şerit yalnız oturumsuz QR sayfalarında (giriş ekranı demo
 * yüzeyi değil — demo giriş paketi) ve akış içinde `sticky` çizilir; kabuk
 * kayması (`.isg-demo-kabuk`) KALKTI. Token şeridin kendi yüksekliğini verir
 * (`--isg-demo-serit-yukseklik`) — ikinci bir sabit yazılmaz; demo kipi
 * kapalıyken şerit çizilmediği için token hiçbir kuralı etkilemez.
 */
export const DEMO_SERIT_YUKSEKLIK_PX = 40;

export const MARKA = {
  /** Ürün adı — kabuk başlığı ve giriş ekranı. */
  /**
   * ✏️ 24.09.2026 — ADLANDIRMA TURU DİLİM 1 (demo ortamı): bundan sonrası
   * ~~**SafeDesk**~~. PDF / e-posta / kılavuz metinlerine DOKUNULMADI — o dilim
   * adlandırma turunun kendisi (session-log). Ürün adı yalnız buradan okunur.
   * ✏️ 01.10.2026 — GÖRÜNEN YÜZ: **SafeOps** (ürün sahibi kararı A, demo
   * taşıma iş emri 3b). Kapsam yalnız kullanıcının GÖRDÜĞÜ yüz: bant logosu ·
   * favicon · sekme başlığı · giriş ekranı (uygulama + Keycloak teması) ·
   * arayüz metni. PDF · e-posta · belgeler · kod tanımlayıcıları · realm/DB
   * adları DEĞİŞMEDİ (ürün adının tamamen değişmesi ayrı iş).
   */
  urunAdi: 'SafeOps',
  /**
   * Bant logosu — koyu zemin sürümü (açık mürekkep, şeffaf zemin; bant iki
   * temada da koyudur, tek dosya yeter). `apps/web/public/` altından servis
   * edilir. ✏️ 01.10.2026: SafeOps paketi (SVG; içsel boyut viewBox oranından
   * köke yazıldı — tarayıcı `naturalWidth/Height`ı oradan okur, `gorsel-dil.spec`
   * oran ölçümü buna dayanır). Eski `SafeDesk_Logo_web*.png` dosyaları
   * SİLİNMEDİ, kullanımdan çıktı (geri dönüş kolaylığı).
   * ~~Küçültülmüş (yalnız işaret) sürüm YOKTUR ve üretilmeyecektir (ürün
   * sahibi, 18.09.2026).~~ ✏️ 01.10.2026: paket simge sürümlerini de veriyor
   * (`safeops-simge*.svg` — public'te durur); bugün onu isteyen yüzey YOK
   * (dar ekranda da tam logo çizilir), bu yüzden token açılmadı.
   */
  logo: '/safeops-logo-koyu-zemin.svg',
  /** Açık zeminli yüzeyler için logo (lacivert mürekkep) — uygulama giriş ekranı, açık tema. */
  logoAcikZemin: '/safeops-logo.svg',
  /**
   * Birincil renk. ~~NÖTR lacivert skala~~ ✏️ 07.10.2026: **`brand`** — platform
   * aksanı (mavi, iki ton; yukarıdaki blok). White-label'da müşterinin
   * değiştirdiği palet budur; `AKSENT` (turuncu) marka yüzeyinin eski aksanı
   * olarak durur, birincil renk değildir.
   */
  anaRenk: 'brand' as const,
  aksent: AKSENT,
} as const;

/**
 * Renk skalası üreticisi: **taban değer `TABAN_BASAMAGI` (6.) basamağa oturur.**
 *
 * Gerekçe teknik ve Mantine'a özgüdür — `primaryShade` kaç ise `variant="filled"`
 * ve `c="..."` (metin) **o basamağı**, `variant="light"` **1. basamağı dolgu /
 * 9. basamağı metin** olarak okur (`get-css-color-variables`). Taban değeri
 * birincil basamağa oturtmak, "yazdığım hex ekranda gördüğüm hex" demektir;
 * alt basamaklar beyazla, üst basamaklar siyahla karıştırılarak TÜRETİLİR —
 * el ile on değer yazılmaz (on değerin dokuzu göz kararı olurdu).
 *
 * ✏️ **07.10.2026: taban 8 → 6.** `brand` paleti verilen değerlerle gelir ve
 * aksanı 6. basamaktadır (`#2563EB`); `primaryShade` ona göre 6'ya indi.
 * ANLAM ve ölçek tabanları **aynı hex'te kalır** — yalnız dizideki yerleri
 * kaydı. Light varyantın metni (9. basamak) eskisinden koyu, dolgusu
 * (1. basamak) eskisi kadar açık: AA kontrastı yalnız yukarı gider.
 */
const BEYAZ_KARISIM = [0.94, 0.86, 0.74, 0.6, 0.44, 0.24] as const;
const SIYAH_KARISIM = [0.86, 0.72, 0.58] as const;

function skala(taban: string): MantineColorsTuple {
  const kanal = [1, 3, 5].map((i) => parseInt(taban.slice(i, i + 2), 16));
  const hex = (v: number[]): string =>
    `#${v.map((k) => Math.round(k).toString(16).padStart(2, '0')).join('')}`;
  const acik = BEYAZ_KARISIM.map((w) => hex(kanal.map((v) => v * (1 - w) + 255 * w)));
  const koyu = SIYAH_KARISIM.map((k) => hex(kanal.map((v) => v * k)));
  const dizi = [...acik, taban, ...koyu];
  if (dizi.length !== 10 || dizi[TABAN_BASAMAGI] !== taban) {
    throw new Error('skala(): taban birincil basamağa oturmadı — BEYAZ/SIYAH karışım sayısı bozuk');
  }
  return dizi as unknown as MantineColorsTuple;
}

/** Lacivert skala — çapanın skalası; kayıtlı kalır (G1 revizyon notu). */
const LACIVERT = skala(CAPA);

/**
 * **KENAR ÇUBUĞU — ÜÇ DURUM ÜÇ AYRI İŞARET** (görsel dil turu dilim 1,
 * 20.09.2026; `.claude/rules/gorsel-dil.md` §10).
 *
 * Turun bulduğu hâl: hover dolgusu Mantine'ın **nötr gri** varsayılanıydı
 * (`gray-0` = `#f8f9fa`), menü zemini ise **kırık beyaz** (`#F6F4EF`) — iki ton
 * iki ayrı yöne gidiyordu ve aralarındaki algısal fark **ΔE 3.6** idi. Aktif
 * satırın ise tek işareti vardı (ΔE 7.9'luk bir dolgu). İkisi de "var"
 * sayılıyor, ikisi de görünmüyordu.
 *
 * ~~⚠️ YENİ RENK TANIMLANMADI — üçü de `lacivert` skalasının basamağıdır.~~
 * ✏️ **REVİZE 07.10.2026 (menü koyu):** dolgular artık koyu menünün kendi
 * token'larından okunur (`MENU_RENKLERI`). 20.09'un *"çapa kayarsa menü onunla
 * yürür"* gerekçesi koyu menüde geçersizdir: menü zemini bandın zeminidir ve
 * marka paletinden (white-label) BAĞIMSIZ sabittir — Kapsam §15.2 revizyonu.
 *
 * ~~⚠️ AKTİF DOLGU HOVER'DAN BİR BASAMAK KOYUDUR.~~ ✏️ **07.10.2026:** aynı
 * dolgu; aktif kalem **yazı rengi (beyaz) + çubuk + ağırlık** ile ayrışır
 * (`MENU_RENKLERI` notu). Dolgu yine tek işaret değildir.
 */
const NAV_HOVER_DOLGU = MENU_HOVER_DOLGU;
const NAV_AKTIF_DOLGU = MENU_AKTIF_DOLGU;

/**
 * **"BURADASIN" ÇUBUĞU — yalnız AKTİF satırda.** ~~Çapanın kendisi (8. basamak)~~
 * ✏️ 07.10.2026: **koyu zemin aksanı `brand[4]`** (`#060A2E` üzerinde 7,6:1) —
 * aksanın menüdeki TEK yeri budur; okunurluk ona bağlı değildir.
 * ⚠️ **KALINLIK YERLEŞİME EKLENMEZ** — `box-shadow: inset` ile verilir (index.css).
 * Menü metninin sol hizası bandaki logonun sol hizasıdır (`MENU_METIN_SOL_PX`)
 * ve `gorsel-dil.spec` bunu **aktif satır üzerinde** ölçer; çubuğu `padding`e
 * ya da `border`a ekleyen kod logoyu da kaydırmak zorunda kalırdı.
 */
const NAV_AKTIF_CUBUK = AKSAN_KOYU_ZEMIN;
export const NAV_CUBUK_KALINLIK_PX = 3;

/**
 * Satır durumları arasındaki geçiş süresi. Anlık değişim *"tıkladım mı,
 * kaydı mı"* belirsizliği bırakır; uzun geçiş menüde gezerken gecikme hissi
 * verir. Değer BURADA durur — `index.css`e yazılsaydı ikinci bir kaynak olurdu.
 */
export const NAV_GECIS_MS = 120;

/**
 * **GRUP BAŞLIĞI — satırdan AĞIRLIK + RENKLE ayrışır, BÜYÜKLÜKLE değil.**
 * Eski `dimmed` (#5D6975) zeminde 5.10:1 ile menü satırlarından **daha soluk**
 * kalıyordu; başlık bir etikettir ama satırın gerisine düşmemelidir — ~~çapada
 * 8.78:1~~ ✏️ 07.10.2026: koyu zeminde `#94A3B8`, **7,5:1** (`MENU_RENKLERI`;
 * yasak ton `#64748B` 4,0:1). Punto büyütmek menüyü bir içindekiler tablosuna
 * çevirirdi.
 */
const NAV_GRUP_BASLIK = MENU_GRUP_BASLIK;

/* ══════════════════════════════════════════════════════════════════════════
   2. G7 — SEMANTİK RENK SETİ (P3'ün kod karşılığı)
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * SAYFA SÖZLEŞMESİ — ROZET ANLAMLARI (P3 · P3-b; Kapsam §20.3b, F4·S5·B1.5).
 *
 * **BEŞ ANLAM KODDA, BUGÜN ÜÇ RENGE BASAR.** *Bölmek pahalı, birleştirmek
 * bedava:* üçe indirip sonra ayırmak, o rengi kullanan **her yeri tek tek**
 * gözden geçirmek demektir; beş anlam durunca `dikkat` ile `gecikmis`i yarın
 * ayırmak **tek satır** değişikliğidir.
 *
 * ⚠️ **P3-a — KIRMIZININ KURALI İKİ KOLLUDUR: ENGEL *VEYA* KABUL EDİLEMEZ
 * SEVİYE.** Risk bandının rozeti hiçbir kapıyı kapatmadığı hâlde kırmızı
 * kalır (§16.12/R5 + R8 kanaryası) — *İSG dilinde kırmızı **riskin
 * ağırlığıdır**, sistemin kapısı değil.*
 *
 * ⚠️ **EKRANDA RENK SABİTLENMEZ.** Ekran `ANLAM.engel` yazar (bir ANLAM adı),
 * `"red"` yazmaz; renk eşlemesi yalnız burada durur.
 */
export type RozetAnlami = 'engel' | 'dikkat' | 'gecikmis' | 'bilgi' | 'olumlu';

/**
 * **G7 — BEŞ ANLAM, BEŞ AYRI SATIR. AKSENTTEN TÜRETİLMEZ.**
 *
 * ⚠️ `dikkat` ve `gecikmis` bugün **AYNI DEĞERİ** taşır (ürün sahibi kararı) —
 * ama **ayrı satırdır**: ayırmak istendiğinde değişecek şey tek bir satırdır,
 * yüz ekran değil. Aynı gerekçe `bilgi` için de geçerli: *bilgi bir DURUM
 * değil, bir ETİKETTİR* → nötr gri; "renksiz"i özel bir dal olarak değil,
 * kendi tokeni olarak taşır.
 *
 * ⚠️ **G3 — SICAKLIK YALNIZ GEOMETRİDE.** Köşeler yumuşadı; **uyarı/tehlike
 * renkleri YUMUŞAMADI.** `engel` doygun kırmızıdır ve öyle kalır.
 *
 * Değerler AA eşiğine göre seçildi (beyaz metinle dolu rozet ≥ 4.5:1;
 * `light` varyantında 9. basamak metin / 1. basamak dolgu ≥ 5.9:1).
 */
const ANLAM_TABANI: Record<RozetAnlami, string> = {
  engel: '#B3261E',
  dikkat: '#9C6600',
  gecikmis: '#9C6600',
  bilgi: '#5D6975',
  olumlu: '#12805C',
};

/**
 * Ekranların yazdığı ANLAM adları. `<Alert color={ANLAM.engel}>` — bir renk
 * değil, bir anlam. (Çıplak dize yerine bu nesne kullanılır ki yazım hatası
 * derlemede yakalansın; Mantine `color` prop'u `string` olduğu için tip
 * güvenliğini sağlayan tek şey budur.)
 */
export const ANLAM: Record<RozetAnlami, RozetAnlami> = {
  engel: 'engel',
  dikkat: 'dikkat',
  gecikmis: 'gecikmis',
  bilgi: 'bilgi',
  olumlu: 'olumlu',
};

/** Anlam → (Mantine renk adı + varyant). Rozet bileşeninin TEK okuduğu yer. */
export const ROZET_RENGI: Record<RozetAnlami, { color: string; variant: string }> = {
  engel: { color: ANLAM.engel, variant: 'filled' },
  dikkat: { color: ANLAM.dikkat, variant: 'light' },
  gecikmis: { color: ANLAM.gecikmis, variant: 'light' },
  bilgi: { color: ANLAM.bilgi, variant: 'light' },
  olumlu: { color: ANLAM.olumlu, variant: 'light' },
};

/* ══════════════════════════════════════════════════════════════════════════
   3. G8 — RİSK ÖLÇEĞİ (P8'in kod karşılığı, madde 107)
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * **DURUM KÜMESİ ≠ ÖLÇEK (P8 — Kapsam §20.3b).** Beş anlam **KATEGORİDİR**;
 * risk bandı gibi **kademeli** şeyler **ÖLÇEKTİR** ve kendi renk dizisini
 * taşır. Bu bir istisna değil, **İKİNCİ BİR SINIFTIR.**
 *
 * ⚠️ **DÖRT KADEME DÖRT AYRI RENK:** yeşil → sarı → **turuncu** → kırmızı.
 * B5'te beş anlam dört bandı ayıramamış, `high` ile `unacceptable` aynı renge
 * düşmüştü; sahada *"yüksek"* **önlem gerektirir**, *"kabul edilemez"* **iş
 * yapılmadan önce indirilmelidir** — farklı aciliyetler, farklı renkler.
 *
 * ⚠️ **ÖLÇEK METODOLOJİYE GÖRE DEĞİŞİR** (5x5 dört bant · Fine-Kinney beş):
 * dizi **kademe sayısıyla** seçilir. Fine-Kinney bu turda İNMEZ — beşinci
 * dizi, o metodoloji geldiği gün **buraya** eklenir (`olcekDizisi` tanımsız
 * kademe sayısında **fail-loud** patlar; sessizce dörde düşmez, çünkü beş
 * bandı dörde sıkıştırmak tam olarak P8'in engellediği bilgi kaybıdır).
 */
const OLCEK_TABANI = {
  olcek1: '#12805C',
  olcek2: '#9C7A00',
  olcek3: '#C25412',
  olcek4: '#A81F1A',
} as const;

/** Dört kademeli ölçek dizisi (5x5 matris) — sırası ANLAMLIDIR. */
const OLCEK_DIZISI_4 = ['olcek1', 'olcek2', 'olcek3', 'olcek4'] as const;

export function olcekDizisi(kademeSayisi: number): readonly string[] {
  if (kademeSayisi === OLCEK_DIZISI_4.length) return OLCEK_DIZISI_4;
  throw new Error(
    `Ölçek dizisi tanımlı değil: ${kademeSayisi} kademe. Yeni bir metodoloji ` +
      "(ör. Fine-Kinney beş bant) geliyorsa dizisi theme.ts'e EKLENİR — " +
      'mevcut diziye sıkıştırılmaz (P8).',
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   4. G5 — TİPOGRAFİ ÖLÇEĞİ · boşluk · G3 geometri · G6 dokunma tabanı
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * **G5 — TİPOGRAFİ İKİNCİ TUR: GÖVDE 16px.** Kademeler TEK fonksiyondan
 * (`kademe`) türer, çarpan 1.125 (majör saniye). Satır yüksekliği, kart iç
 * boşluğu, düğme yüksekliği de **bu ölçekten** akar — ekran ekran boyut
 * yazılmaz. Dizi:
 *   xs 14.2 → sm/gövde 16 → md 18 → lg 20.3 → xl 22.8 → h3 25.6 → h2 28.8 → h1 32.4
 */
const OLCEK_CARPANI = 1.125;
const GOVDE_PX = 16;
const kademe = (n: number): string => `${((GOVDE_PX * OLCEK_CARPANI ** n) / 16).toFixed(4)}rem`;

/** Boşluk da ölçekten akar: metin büyürken boşluk büyümezse yerleşim sıkışır. */
const bosluk = (carpan: number): string => `${((GOVDE_PX * carpan) / 16).toFixed(4)}rem`;

/**
 * Boşluk adlarının çarpanları — **tema ölçeği ve px okuyucusu AYNI haritadan**
 * beslenir. İki yerde yazılsaydı biri değişince öteki sessizce eskirdi.
 */
const BOSLUK_CARPANI = { xs: 0.5, sm: 0.75, md: 1, lg: 1.5, xl: 2.25 } as const;
export type BoslukAdi = keyof typeof BOSLUK_CARPANI;
/** Ölçek adının px karşılığı — **yerleşim hesabı yapan kod** için (ekran sayı yazmaz). */
export const boslukPx = (ad: BoslukAdi): number => GOVDE_PX * BOSLUK_CARPANI[ad];

/**
 * **SOL HİZA — LOGO, MENÜ METNİYLE AYNI DİKEY ÇİZGİDE.**
 *
 * Menü metninin sol hizası iki ölçek değerinin TOPLAMIDIR: kenar çubuğunun
 * dış payı (`AppShell.Navbar p`) + menü satırının iç payı (Mantine
 * `NavLink` yatay dolgusu = `var(--mantine-spacing-sm)`, `NavLink.css`).
 * Bant kendi iç payını (`AppShell.Header px`) sağdaki düğmelerle PAYLAŞIR —
 * o yüzden bant payına dokunulmaz, **yalnız logo** aradaki farkı kapatır.
 *
 * ⚠️ **20 SİHİRLİ SAYI DEĞİLDİR:** menü hizası bir gün değişirse (ölçek ya da
 * `p` değeri) logo onunla yürür. Kanarya tarayıcıda ölçer — `gorsel-dil.spec`
 * logonun sol kenarını menü ETİKETİNİN sol kenarıyla karşılaştırır.
 */
export const BANT_IC_PAY: BoslukAdi = 'md';
export const MENU_DIS_PAY: BoslukAdi = 'xs';
/** Mantine `NavLink`in yatay dolgusu — onun CSS'i `spacing.sm` okur. */
export const MENU_SATIR_IC_PAY: BoslukAdi = 'sm';
export const MENU_METIN_SOL_PX = boslukPx(MENU_DIS_PAY) + boslukPx(MENU_SATIR_IC_PAY);
/** Bandın iç payı ile menü hizası arasındaki fark — logonun sol kayması. */
export const BANT_LOGO_SOL_KAYMA_PX = MENU_METIN_SOL_PX - boslukPx(BANT_IC_PAY);

/**
 * **DAR EKRAN — KABUK KIRILMA NOKTASI (§11.2; ürün sahibi 21.09.2026).**
 *
 * Bu sabitin ALTINDA: menü bir çekmecedir (`navbar.collapsed.mobile`), bantta
 * Burger görünür, rol etiketi ile *Çıkış yap* çekmeceye taşınır.
 * ÜSTÜNDE: bugünkü masaüstü görünümü, **birebir**.
 *
 * ⚠️ **TEK SABİT, İKİ KABUK.** Değer önceden `AppShellLayout` ve
 * `PortalShellLayout` içinde AYRI AYRI `'xs'` literali olarak yazılıydı;
 * kopyanın tek tarafta değişmesi iki kabuğu sessizce ayrıştırırdı
 * (§2.31a'nın kabuk ölçeğindeki kardeşi). Menünün çekmeceye dönmesi ve
 * bandın sadeleşmesi de AYNI değerden okunur — iki kural iki sabitten
 * tetiklenirse aralarında "menü sabit ama bant kırık" bölgesi kalır
 * (ölçülen: eski hâlde 576–729 px arası, 150 px).
 *
 * ⚠️ ~~**NEDEN `sm` (768):** 768'de menü açıkken içerik **496px** kalır ve Genel
 * bakış · Çalışanlar · İş izinleri yatay kaydırma İSTEMEZ (ölçüldü). Bandın
 * ölçülen kırılma eşiği 729px'tir — `sm` onu güvenli tarafta bırakır.~~
 *
 * ✏️ **REVİZE 21.09.2026 (ürün sahibi — `sm` → `md`): KIRILMA NOKTASI CİHAZ
 * SINIFININ İÇİNDEN GEÇMEZ.** Gerekçe 1c ölçümüdür: `sm`de 744px'lik tablet
 * çekmeceye giriyor ve içeriğe **712px** veriyordu, 768'lik tablet ise sabit
 * menüyle **496px**e düşüyordu — *aynı cihaz sınıfında iki farklı yerleşim ve
 * BÜYÜK TABLET DAHA DAR.* `md` ile dikey tabletlerin tamamı (744-834) çekmece
 * moduna girer; sabit menü yatay tablete (1024+) ve masaüstüne kalır.
 */
export const KABUK_KIRILMA: MantineBreakpoint = 'md';

/**
 * **BANDIN BİRİNCİ SADELEŞME BASAMAĞI — ROL ETİKETİ + *ÇIKIŞ YAP* ÇEKMECEYE.**
 *
 * ⚠️ **BU EŞİK `KABUK_KIRILMA`DAN AYRIDIR VE AYRI KALMALIDIR** (revizyon
 * 21.09.2026): menünün eşiği bir **cihaz sınıfı** kararıdır, bandın eşikleri
 * **bandın kendi sığma ölçüsüdür**. İkisi bir süre aynı değerdi (`sm`) ve o
 * zaman ayrı durmaları gereksiz görünüyordu; `md`ye geçişte bağlılık
 * korunsaydı rol etiketi ile *Çıkış yap* **992'nin altında** çekmeceye
 * inerdi — oysa 820'de bant Burger'le birlikte bile **676px** ister ve
 * rahatça sığar (ölçüldü). Bilgi, yer kalmadığı için taşınır; menü
 * çekmeceye döndüğü için değil.
 */
export const BANT_ROL_ESIGI: MantineBreakpoint = 'sm';

/**
 * **BANDIN İKİNCİ SADELEŞME BASAMAĞI (§11.1 önem sırası).**
 *
 * `KABUK_KIRILMA` altında rol etiketi ve *Çıkış yap* çekmeceye taşınır; bant
 * o hâliyle **530px**e kadar tek satır kalır (ölçüldü). Daha darında holding
 * rozeti de taşınır — bant **422px**e iner ve 440'ta sığar.
 * ⚠️ **Bu bir "ikinci kırılma noktası" DEĞİLDİR:** menü davranışı buradan
 * okunmaz, yalnız bandın en düşük öncelikli öğesi gider.
 */
export const BANT_ROZET_ESIGI: MantineBreakpoint = 'xs';

/**
 * **BANDIN ÜÇÜNCÜ SADELEŞME BASAMAĞI — İŞLERİM İKON + SAYIYA İNER (§11.1).**
 *
 * Bu eşik bir kırılma noktası ADI DEĞİL, ÖLÇÜLEN BİR PİKSELDİR ve olması
 * gereken de budur: bandın sığma sınırı ekranın değil **bandın kendi
 * içeriğinin** ölçüsüdür (logo 169 + Burger 44 + zil 48 + paylar). Mantine'ın
 * `xs`i (576) buraya hiçbir şekilde denk düşmez.
 *
 * ⚠️ **DEĞER ÖLÇÜMDEN GELİR:** metinli *İşlerim* düğmesiyle bant **436px**
 * ister (16 + 44 Burger + 12 + 169 logo + 12 + 99 İşlerim + 12 + 48 zil + 16).
 * Altında zil sağ paya taşar. Düğme ikona inince gereken **385px**e düşer;
 * 375'te kalan 10px iki grubun arasındaki nefestir.
 */
export const BANT_ISLERIM_ESIGI_PX = 436;

/** Kenar çubuğunun masaüstü genişliği — iki kabuk de bunu okur. */
export const MENU_GENISLIK_PX = 240;

/**
 * **G3 — GEOMETRİ SICAK.** Kart 12 · kontrol 8 · rozet hap (pill).
 * ⚠️ Sıcaklık **yalnız geometridedir**; renkler yumuşamaz (G7 bloğu).
 */
export const YARICAP = { kontrol: 8, kart: 12, rozet: 9999 } as const;

/**
 * **G6 — DOKUNMA HEDEFİ TABANI: 44px.**
 *
 * Gerekçe (üç kaynak aynı yeri gösteriyor): **WCAG 2.2 · 2.5.5 (AAA)
 * 44×44 CSS px** · **Apple HIG 44pt** · eldivenli parmağın temas alanı çıplak
 * parmaktan geniştir (~8-9 mm → ~11-13 mm) ve 44 CSS px tipik saha
 * cihazında ≈ 11.6 mm'ye denk gelir. **Metnin 16px olması yetmez** — okunan
 * şeyle dokunulan şey ayrı ölçülerdir; 32px bir düğme masabaşında çalışır,
 * eldivenle çalışmaz.
 *
 * ⚠️ **TABAN İSTİSNASIZDIR — "compact" boyutlar dahil (G4: tek yüzey).**
 * Satır içi ikincil eylemler dar yazı tipini ve dar yatay boşluğunu korur,
 * ama **yükseklikte tabana çıkar**: iki ayrı taban tanımlamak, sahadaki
 * kullanıcıyı ekranın hangi köşesine bastığına göre cezalandırırdı.
 */
export const DOKUNMA_TABANI_PX = 44;
const dokunma = `${DOKUNMA_TABANI_PX}px`;

const fontSans = "'IBM Plex Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif";
const fontMono = "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

/* ══════════════════════════════════════════════════════════════════════════
   5. Tema + CSS değişkenleri
   ══════════════════════════════════════════════════════════════════════════ */

export const theme = createTheme({
  // AZALTILMIŞ HAREKET (madde 277, 29.09.2026): işletim sisteminde hareket
  // azaltmayı seçmiş kullanıcıda Mantine'ın geçişli bileşenleri (Transition ·
  // Collapse · Modal · Menu · Popover) süreyi sıfırlar. Varsayılanı KAPALIDIR;
  // açılmadıkça Mantine tercihi hiç okumaz. ⚠️ `AppShell` bu anahtarı OKUMAZ
  // (geçişi kendi CSS değişkeninden) — kabuğun kuralı index.css'tedir.
  respectReducedMotion: true,
  primaryColor: MARKA.anaRenk,
  // ✏️ 07.10.2026: 8 → TABAN_BASAMAGI (6) — `brand[6]` açık zeminin aksanı;
  // `skala()` ürün renklerinin tabanını aynı basamağa oturtur.
  primaryShade: TABAN_BASAMAGI,
  colors: {
    brand: BRAND_PALETI,
    lacivert: LACIVERT,
    engel: skala(ANLAM_TABANI.engel),
    dikkat: skala(ANLAM_TABANI.dikkat),
    gecikmis: skala(ANLAM_TABANI.gecikmis),
    bilgi: skala(ANLAM_TABANI.bilgi),
    olumlu: skala(ANLAM_TABANI.olumlu),
    olcek1: skala(OLCEK_TABANI.olcek1),
    olcek2: skala(OLCEK_TABANI.olcek2),
    olcek3: skala(OLCEK_TABANI.olcek3),
    olcek4: skala(OLCEK_TABANI.olcek4),
  },
  fontFamily: fontSans,
  fontFamilyMonospace: fontMono,
  fontSizes: {
    xs: kademe(-1),
    sm: kademe(0),
    md: kademe(1),
    lg: kademe(2),
    xl: kademe(3),
  },
  lineHeights: { xs: '1.4', sm: '1.5', md: '1.55', lg: '1.6', xl: '1.6' },
  headings: {
    fontFamily: fontSans,
    fontWeight: '600',
    sizes: {
      h1: { fontSize: kademe(6) },
      h2: { fontSize: kademe(5) },
      h3: { fontSize: kademe(4), lineHeight: '1.3' },
      h4: { fontSize: kademe(3) },
      h5: { fontSize: kademe(2) },
      h6: { fontSize: kademe(1) },
    },
  },
  /** Boşluk ölçeği — gövde punto'sunun katları (G5: tek yerden büyür). */
  spacing: {
    xs: bosluk(BOSLUK_CARPANI.xs),
    sm: bosluk(BOSLUK_CARPANI.sm),
    md: bosluk(BOSLUK_CARPANI.md),
    lg: bosluk(BOSLUK_CARPANI.lg),
    xl: bosluk(BOSLUK_CARPANI.xl),
  },
  radius: {
    xs: '4px',
    sm: '6px',
    md: `${YARICAP.kontrol}px`,
    lg: `${YARICAP.kart}px`,
    xl: '16px',
  },
  defaultRadius: 'md',
  components: {
    // Kart: beyaz yüzey + 1px kenarlık, GÖLGE YOK, köşe 12 (G3).
    Card: Card.extend({
      defaultProps: { withBorder: true, shadow: undefined, radius: YARICAP.kart },
    }),
    Button: Button.extend({ defaultProps: { radius: 'md' } }),
    ActionIcon: ActionIcon.extend({ defaultProps: { radius: 'md' } }),
    // Rozet HAP biçimlidir (G3) — kademeli ölçek de dahil.
    Badge: Badge.extend({ defaultProps: { radius: YARICAP.rozet } }),
    // KAPALI İÇERİK AYNI ÇİZİMDE, `display: none` İLE TUTULUR (sol menü
    // gruplama, 29.09.2026 — CI run 36557968673). Mantine'ın varsayılanı React
    // `Activity`dir ve GİZLİ alt ağaç ERTELENMİŞ çizilir: menü grubunun başlığı
    // DOM'a girer, satırları sonraki bir çizimde gelir. O pencerede
    // *"bu satır menüde yok"* iddiası (`toHaveCount(0)`) HER ZAMAN doğru çıkar —
    // düşen test değil, sessizce geçen test üretir. Tek kullanıcısı menü
    // gruplarıdır (`NavLink` iç `Collapse`i bu varsayılanı okur).
    Collapse: Collapse.extend({ defaultProps: { keepMountedMode: 'display-none' } }),
    // Tablo satır boşluğu token'dan; ekranlarda tek tek yazılmaz.
    Table: Table.extend({ defaultProps: { verticalSpacing: 'sm', horizontalSpacing: 'sm' } }),
  },
});

/**
 * **CSS DEĞİŞKENLERİ — TS sabitlerinin TEK aynası.**
 *
 * Mantine bu haritayı kendi `:root` bloğuna basar; `index.css` yalnız BU
 * değişkenleri okur, hiçbir renk/ölçü değeri taşımaz.
 *
 * ⚠️ **DOKUNMA TABANI NEDEN BURADA DEĞİL, index.css'te UYGULANIR:** Mantine'ın
 * boyut değişkenleri (`--button-height-*`, `--ai-size-*`, `--input-height-*`)
 * `:root`ta değil **bileşenin kendi sınıfında** tanımlıdır; `:root`tan verilen
 * aynı isim **özgüllükte kaybeder ve sessizce yok sayılır** (bu turda bir kez
 * düştü — düğme 44 yerine 30 çizildi). Değer yine BURADAN gelir
 * (`--isg-dokunma-tabani`); uygulanması seçici gerektirdiği için CSS'tedir.
 */
export const isgCssDegiskenleri: CSSVariablesResolver = () => ({
  variables: {
    '--isg-zemin': ZEMIN,
    '--isg-capa': CAPA,
    '--isg-capa-uzeri-metin': CAPA_UZERI_METIN,
    '--isg-capa-uzeri-ikincil': CAPA_UZERI_IKINCIL,
    '--isg-menu-zemin': MENU_ZEMIN,
    '--isg-yuzey': YUZEY,
    '--isg-kenarlik': KENARLIK,
    '--isg-metin-ana': METIN_ANA,
    '--isg-metin-ikincil': METIN_IKINCIL,
    // Kenar çubuğu — üç durum üç işaret (gorsel-dil.md §10); ✏️ 07.10.2026 koyu menü.
    '--isg-nav-hover-dolgu': NAV_HOVER_DOLGU,
    '--isg-nav-aktif-dolgu': NAV_AKTIF_DOLGU,
    '--isg-nav-aktif-cubuk': NAV_AKTIF_CUBUK,
    '--isg-nav-aktif-metin': MENU_AKTIF_METIN,
    '--isg-nav-cubuk-kalinlik': `${NAV_CUBUK_KALINLIK_PX}px`,
    '--isg-nav-grup-baslik': NAV_GRUP_BASLIK,
    '--isg-nav-gecis': `${NAV_GECIS_MS}ms`,
    '--isg-menu-metin': MENU_METIN,
    // Platform aksanı — koyu zeminde (bant · menü) bağlantı rengi (gorsel-dil.md §14).
    '--isg-aksan-koyu-zemin': AKSAN_KOYU_ZEMIN,
    '--isg-aksent': AKSENT,
    '--isg-bant-zemin': BANT_ZEMIN,
    // Demo şeridi: token her zaman ÜRETİLİR; okuyucusu yalnız DemoSeridi (giriş ekranı ve kabuk okumaz — 25.09.2026).
    '--isg-demo-serit-yukseklik': `${DEMO_SERIT_YUKSEKLIK_PX}px`,
    '--isg-dokunma-tabani': dokunma,
    '--isg-font-sans': fontSans,
    '--isg-font-mono': fontMono,
    '--isg-etiket-punto': kademe(-2),
    // madde 220 / R8 — TEK SATIR YATAY KAYDIRMALI TABLO: sola sabit kimlik
    // kolonlarının genişliği (ikinci sabit kolonun `left` ofseti de budur —
    // iki değer ayrı yazılsaydı kaydırınca kolonlar üst üste binerdi) ve satır
    // içi giriş alanlarının taban genişlikleri. Ölçekten akar (G5).
    '--isg-sabit-kolon-genislik': bosluk(13),
    // ✏️ GEZİNTİ ARTÇISI 3 / B1: metin kolonu SABİT genişlik (hücre sarar, aşağı
    // büyür); skor kolonu dar sabit (tek haneli değer + kısa rozet).
    '--isg-satir-metin-genislik': bosluk(14),
    '--isg-satir-skor-genislik': bosluk(4.5),
    '--isg-satir-olcek-genislik': bosluk(6.5),
    // Mantine köprüleri (eski `body` hack'inin yerine — yukarıdaki nota bak).
    '--mantine-color-body': ZEMIN,
    '--mantine-color-text': METIN_ANA,
    '--mantine-color-dimmed': METIN_IKINCIL,
    '--mantine-color-default-border': KENARLIK,
  },
  light: {},
  dark: {},
});
